// Where a new person is in the first-run steps, for the Get started bar on
// every page and the sidebar's way back to it (founder, 2026-09-28: "a
// proper website wide stepper ... to add resume and complete this
// questionnaire"; Resume → About you → Questions → Apply). The steps are the
// server's (Node Backend services/profile/gettingStarted.js), read off the
// same facts as the About you page, and read again whenever one changes.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { fetchSteps } from '../services/aboutYou';
import { FACTS_CHANGED } from '../services/aboutYouEvents';

const Context = createContext({
  loaded: false, steps: [], current: null, complete: false, hidden: false,
  hide: () => {}, show: () => {}, refresh: () => {},
});
export const useGettingStarted = () => useContext(Context);

// Hidden with its ×, per person and per browser: a convenience, and the
// sidebar brings it back. Storage may be blocked; the bar then just shows.
const key = (uid) => `aa.getStarted.hidden.${uid}`;
const readHidden = (uid) => { try { return window.localStorage.getItem(key(uid)) === '1'; } catch { return false; } };
const writeHidden = (uid, hidden) => {
  try {
    if (hidden) window.localStorage.setItem(key(uid), '1');
    else window.localStorage.removeItem(key(uid));
  } catch { /* storage blocked */ }
};

// Read again on a page change once this old: an application sent from the
// Jobs page finishes the last step and says so nowhere else.
const STALE_MS = 30_000;

export function GettingStartedProvider({ children }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;
  const { pathname } = useLocation();
  const [steps, setSteps] = useState(null);
  const [hidden, setHidden] = useState(false);
  const readAt = useRef(0);
  const soon = useRef(null);

  const refresh = useCallback(() => {
    if (!uid) return;
    readAt.current = Date.now();
    fetchSteps().then(setSteps).catch(() => {});
  }, [uid]);

  useEffect(() => {
    setSteps(null);
    setHidden(uid ? readHidden(uid) : false);
    refresh();
  }, [uid, refresh]);

  // A fact changed somewhere (the setup, About you, the auto-apply switch, a
  // resume): read again once a burst of changes has settled.
  useEffect(() => {
    const later = () => { clearTimeout(soon.current); soon.current = setTimeout(refresh, 1200); };
    const events = [FACTS_CHANGED, 'refresh-profile', 'active-resume-changed'];
    events.forEach(e => window.addEventListener(e, later));
    return () => {
      clearTimeout(soon.current);
      events.forEach(e => window.removeEventListener(e, later));
    };
  }, [refresh]);

  useEffect(() => {
    if (steps && !steps.complete && Date.now() - readAt.current > STALE_MS) refresh();
    // Only on a change of page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const hide = useCallback(() => { setHidden(true); if (uid) writeHidden(uid, true); }, [uid]);
  const show = useCallback(() => { setHidden(false); if (uid) writeHidden(uid, false); }, [uid]);

  const value = useMemo(() => ({
    loaded: Boolean(steps),
    steps: steps?.steps || [],
    current: steps?.current || null,
    complete: Boolean(steps?.complete),
    hidden,
    hide,
    show,
    refresh,
  }), [steps, hidden, hide, show, refresh]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}
